import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import type { ContentItem } from '../../types/content';

interface FaqEntry {
  question: string;
  answer: string;
}

interface ContactInfo {
  phone?: string;
  email?: string;
  address?: string;
  hours?: string;
}

interface PageContent {
  title: string;
  description: string;
  faqs: FaqEntry[];
  contact: ContactInfo;
}

const defaultContent: PageContent = {
  title: 'Support',
  description: "We're here to help. Find answers to common questions or get in touch.",
  faqs: [],
  contact: {},
};

function parseContent(item: ContentItem | undefined): any {
  if (!item?.content) return null;
  try {
    return typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
  } catch {
    return item.content;
  }
}

function getItem(section: any, title: string): ContentItem | undefined {
  return section?.content_items?.find(
    (i: ContentItem) => i.title === title && i.status === 'Published'
  );
}

export default function MobileSupport() {
  const [pageContent, setPageContent] = useState<PageContent>(defaultContent);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data: sections } = await supabase
        .from('content_sections')
        .select('id, title, content_items (*)')
        .eq('page', 'support');

      if (sections) {
        const headerSection = sections.find(s => s.title === 'Page Header');
        const faqSection = sections.find(s => s.title === 'FAQs');
        const contactSection = sections.find(s => s.title === 'Contact Support');

        const parsedFaqs = parseContent(getItem(faqSection, 'FAQ List'));
        const parsedContact = parseContent(getItem(contactSection, 'Contact Details'));

        setPageContent({
          title: parseContent(getItem(headerSection, 'Title')) || defaultContent.title,
          description: parseContent(getItem(headerSection, 'Description')) || defaultContent.description,
          faqs: Array.isArray(parsedFaqs) ? parsedFaqs : [],
          contact: parsedContact && typeof parsedContact === 'object' ? parsedContact : {},
        });
      }
    } catch (err) {
      console.error('Error fetching support content:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasFaqs = pageContent.faqs.length > 0;
  const hasContact = Object.values(pageContent.contact).some(Boolean);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-8">
      {/* Header card */}
      <div
        className="relative rounded-2xl overflow-hidden mb-6 h-32 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/1325681/pexels-photo-1325681.jpeg?auto=compress&w=800)' }}
      >
        <div className="absolute inset-0 bg-golf-green/80" />
        <div className="relative z-10 p-4 h-full flex flex-col justify-center">
          <p className="text-golf-sand text-xs font-medium uppercase tracking-wide">Help Center</p>
          <p className="text-white font-display font-bold text-xl">{pageContent.title}</p>
          <p className="text-golf-sand text-xs mt-0.5 line-clamp-1">{pageContent.description}</p>
        </div>
      </div>

      {/* FAQs */}
      {hasFaqs && (
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 px-1">
            Frequently Asked Questions
          </p>
          <div className="space-y-2">
            {pageContent.faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-4 py-3.5 text-left active:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-800 text-sm pr-3 leading-snug">{faq.question}</span>
                  {openFaq === idx
                    ? <ChevronUpIcon className="w-4 h-4 text-golf-orange flex-shrink-0" />
                    : <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  }
                </button>
                {openFaq === idx && (
                  <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-50 pt-3">
                    {faq.answer}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Contact */}
      {hasContact && (
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 px-1">Contact</p>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {pageContent.contact.phone && (
              <a
                href={`tel:${pageContent.contact.phone}`}
                className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-50 active:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 bg-golf-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <PhoneIcon className="w-5 h-5 text-golf-orange" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Phone</p>
                  <p className="text-sm font-medium text-gray-800">{pageContent.contact.phone}</p>
                </div>
              </a>
            )}
            {pageContent.contact.email && (
              <a
                href={`mailto:${pageContent.contact.email}`}
                className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-50 active:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 bg-golf-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <EnvelopeIcon className="w-5 h-5 text-golf-orange" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Email</p>
                  <p className="text-sm font-medium text-gray-800">{pageContent.contact.email}</p>
                </div>
              </a>
            )}
            {pageContent.contact.address && (
              <div className="flex items-center gap-3 px-4 py-3.5 active:bg-gray-50 transition-colors">
                <div className="w-9 h-9 bg-golf-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPinIcon className="w-5 h-5 text-golf-orange" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Address</p>
                  <p className="text-sm font-medium text-gray-800">{pageContent.contact.address}</p>
                </div>
              </div>
            )}
          </div>
          {pageContent.contact.hours && (
            <p className="text-xs text-gray-400 text-center mt-3">{pageContent.contact.hours}</p>
          )}
        </div>
      )}

      {!hasFaqs && !hasContact && (
        <div className="text-center py-16 text-gray-400 text-sm">
          Support content coming soon.
        </div>
      )}
    </div>
  );
}
